import React, { useEffect, useState } from 'react'
import { Modal, ModalBody, Spinner } from 'reactstrap'
import { APIClient } from 'helpers/api_helper'

/**
 * Kabin cirosunu sıfırlama onayı (müşteri talebi 2026-08-14).
 *
 * PARA VERİSİ olduğu için akış bilinçli olarak iki adımlı:
 *  1. Modal açılır açılmaz `dryRun` çağrılır → kullanıcı KAÇ KAYIT ve NE TUTAR
 *     sıfırlanacağını BASMADAN ÖNCE görür. (Hiçbir şey yazılmaz.)
 *  2. Onaydan sonra gerçek çağrı yapılır ve dönen `deletedBatch` damgası
 *     ekranda gösterilir — geri alma bu damgayla yapılır.
 *
 * Kayıtlar SİLİNMEZ, gizlenir; ciro raporu ve genel toplam düşer ama
 * müşterilerin foto indirme / cepten baskı linkleri etkilenmez.
 */

interface IProps {
  show: boolean
  boothId: string | null
  boothName: string
  onClose: () => void
  onDone: (msg: string) => void
}

const SalesResetModal: React.FC<IProps> = ({
  show,
  boothId,
  boothName,
  onClose,
  onDone
}) => {
  const [loading, setLoading] = useState(false)
  const [working, setWorking] = useState(false)
  const [preview, setPreview] = useState<{ count: number; totalAmount: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [batch, setBatch] = useState<string | null>(null)

  // Modal her açıldığında önizlemeyi TAZE çek (bayat sayı gösterilmesin)
  useEffect(() => {
    if (!show || !boothId) return
    setPreview(null)
    setError(null)
    setBatch(null)
    setLoading(true)
    const api = new APIClient()
    api
      .create(`/api/v1/payments/sales/reset/${boothId}?dryRun=1`, {})
      .then((res: any) => {
        if (res?.ok) setPreview({ count: res.count, totalAmount: res.totalAmount })
        else setError(res?.error || 'Önizleme alınamadı.')
      })
      .catch((e: any) => setError(typeof e === 'string' ? e : 'Önizleme alınamadı.'))
      .finally(() => setLoading(false))
  }, [show, boothId])

  const handleConfirm = async () => {
    if (!boothId) return
    setWorking(true)
    setError(null)
    try {
      const api = new APIClient()
      const res: any = await api.create(`/api/v1/payments/sales/reset/${boothId}`, {})
      if (res?.ok) {
        setBatch(res.batch || null)
        onDone(
          res.count > 0
            ? `${boothName}: ${res.count} satış kaydı sıfırlandı (₺${res.totalAmount}).`
            : `${boothName}: sıfırlanacak satış yoktu.`
        )
      } else {
        setError(res?.error || 'Sıfırlama başarısız.')
      }
    } catch (e: any) {
      setError(typeof e === 'string' ? e : 'Sıfırlama başarısız.')
    } finally {
      setWorking(false)
    }
  }

  return (
    <Modal isOpen={show} toggle={onClose} centered>
      <ModalBody className='p-4'>
        <div className='text-center mb-3'>
          <i className='mdi mdi-cash-remove display-5 text-danger'></i>
          <h5 className='mt-2 mb-1'>Kabin cirosunu sıfırla</h5>
          <p className='text-muted mb-0'>{boothName}</p>
        </div>

        {loading && (
          <div className='text-center py-3'>
            <Spinner size='sm' /> <span className='ms-2'>Hesaplanıyor…</span>
          </div>
        )}

        {!loading && preview && !batch && (
          <>
            <div className='bg-light rounded p-3 mb-3 text-center'>
              <div className='fs-4 fw-semibold'>
                {preview.count} satış kaydı
              </div>
              <div className='text-muted'>
                Toplam ₺{preview.totalAmount}
              </div>
            </div>
            <p className='text-muted small mb-0'>
              Bu kayıtlar <strong>silinmez</strong>, ciro raporundan gizlenir.
              Kabinin cirosu ve genel toplam bu tutar kadar düşer. Müşterilerin
              fotoğraf indirme ve cepten baskı bağlantıları <strong>etkilenmez</strong>.
              İşlem geri alınabilir.
            </p>
          </>
        )}

        {batch && (
          <div className='alert alert-success mb-0'>
            <div className='fw-semibold mb-1'>Sıfırlandı.</div>
            <div className='small mb-1'>Geri almak için gereken damga:</div>
            <code className='d-block text-break small'>{batch}</code>
          </div>
        )}

        {error && <div className='alert alert-danger mt-3 mb-0'>{error}</div>}

        <div className='d-flex gap-2 mt-4'>
          <button
            type='button'
            className='btn btn-light flex-fill'
            onClick={onClose}
            disabled={working}
          >
            {batch ? 'Kapat' : 'Vazgeç'}
          </button>
          {!batch && (
            <button
              type='button'
              className='btn btn-danger flex-fill'
              onClick={handleConfirm}
              disabled={working || loading || !preview || preview.count === 0}
            >
              {working ? <Spinner size='sm' /> : 'Sıfırla'}
            </button>
          )}
        </div>
      </ModalBody>
    </Modal>
  )
}

export default SalesResetModal
